import React, {
  useState,
  useCallback,
  useContext,
  useMemo,
  useEffect,
} from 'react';
import PropTypes from 'prop-types';
import { MenuItem } from '@mui/material';
import { GroupsOutlined } from '@mui/icons-material';
import format from 'string-template';

import DialogRoundedModal, { ModalBody } from '../../../../../components/DialogRoundedModal';
import InputLabel from '../../../../../components/v2/InputLabel';
import TextField from '../../../../../components/v2/TextField';
import { SaveButton } from '../../../../../components/Button/ActionButtons';
import useStorage from '../../../../../hooks/useStorage';
import UserContext from '../../../../../context/UserContext';
import LoggedInUserContext from '../../../../../context/LoggedInUserContext';
import ManageClientContext from '../../../../context/ManageClientContext';
import useToast from '../../../../hooks/useToast';
import LoadingOverlay from '../../../../components/LoadingOverlay';
import AttachmentsContainer from '../../../../components/AttachmentsContainer';
import Announcement, { ALL_CLIENTS_TAG } from '../../../../Model/Announcement';
import EmojiInput from '../../../Chat/components/EmojiInput';

import {
  processAttachments,
  onFilesSelected,
  MAX_FILE_UPLOAD_SIZE,
  ErrorTypes,
} from './utils';
import { RecipientActionsContainer, StyledSelectField, StyledTypography } from './styles';
import texts from './texts.json';

const MAX_MESSAGE_LENGTH = 500;

const AnnouncementModal = ({
  isOpen,
  onDidDismiss,
  announcement,
}) => {
  const [announcementMessage, setAnnouncementMessage] = useState('');
  const [isProcessing, setIsProcessing] = useState(false);
  const [selectedTag, setSelectedTag] = useState(ALL_CLIENTS_TAG.id);
  const [fileAttachments, setFileAttachments] = useState([]);

  const { userId: loggedInUserId } = useContext(LoggedInUserContext);
  const { userId: coachId } = useContext(UserContext);
  const { clientTagsDocs } = useContext(ManageClientContext);

  const {
    uploadAttachments,
    deleteAttachments,
  } = useStorage();

  const { showToast } = useToast();

  const isEdit = !!announcement;

  // Populate the modal with the announcement data in case of an edit
  useEffect(() => {
    if (isEdit) {
      const { message, tagId, attachments } = announcement;

      setAnnouncementMessage(message);
      setSelectedTag(tagId);
      if (attachments?.length) {
        // Set the file attachments in a way that is compatible with the AttachmentsContainer component
        setFileAttachments(attachments.map((attachment) => ({
          name: attachment.title,
          type: attachment.mimeType,
          size: attachment.fileSize,
          url: attachment.assetPath,
        })));
      }
    }
  }, [isEdit, announcement]);

  const clientTagsList = useMemo(() => ([
    {
      id: ALL_CLIENTS_TAG.id,
      label: ALL_CLIENTS_TAG.name,
    },
    ...clientTagsDocs.map((doc) => ({
      id: doc.id,
      label: doc.tag,
    })),
  ]), [clientTagsDocs]);

  const handleFileSelection = useCallback((event) => {
    const { error, files } = onFilesSelected(event);
    if (files.length) {
      setFileAttachments((prevFiles) => [...prevFiles, ...files]);
    } else if (error === ErrorTypes.FILE_SIZE) {
      showToast(format(texts.toast.fileSizeError, {
        maxFileSize: MAX_FILE_UPLOAD_SIZE / 1000000, // Bytes to MB
      }), { error: true });
    } else if (error === ErrorTypes.AUDIO_FILE) {
      showToast(texts.toast.audioFileError, { error: true });
    }
  }, [showToast]);

  const removeFile = useCallback((index) => {
    const resultAttachments = [...fileAttachments];
    resultAttachments.splice(index, 1);
    setFileAttachments(resultAttachments);
  }, [fileAttachments]);

  const isMessageValid = announcementMessage.trim().length <= MAX_MESSAGE_LENGTH;

  const handleSaveAnnouncement = useCallback(async () => {
    if (!announcementMessage.trim()) {
      showToast(texts.toast.empty, { error: true });
      return;
    }

    try {
      setIsProcessing(true);

      const { attachments, error } = await processAttachments({
        fileAttachments,
        coachId,
        loggedInUserId,
        uploadAttachments,
        deleteAttachments,
      });

      if (error) {
        showToast(texts.toast.uploadFailed, { error: true });
        // Restore the existing attachments and delete the uploaded ones in case of an upload failure
        setFileAttachments(attachments);
        return;
      }

      const tagName = clientTagsList.find((tag) => tag.id === selectedTag)?.label;

      if (isEdit) {
        await announcement.updateDoc({
          message: announcementMessage.trim(),
          attachments,
          tagId: selectedTag,
          tagName,
          lastUpdatedAt: new Date(),
          lastUpdatedBy: loggedInUserId,
        });
      } else {
        await Announcement.addDoc({
          message: announcementMessage.trim(),
          coach: coachId,
          createdAt: new Date(),
          createdBy: loggedInUserId,
          likes: 0,
          tagId: selectedTag,
          tagName,
          attachments,
          isRemoved: false,
        });
      }

      showToast(texts.toast.success, { success: true });
      onDidDismiss();
    } catch (error) {
      showToast(format(texts.toast.failed, { error: error.message }), { error: true });
    } finally {
      setIsProcessing(false);
    }
  }, [
    announcementMessage,
    showToast,
    onDidDismiss,
    coachId,
    loggedInUserId,
    selectedTag,
    clientTagsList,
    fileAttachments,
    uploadAttachments,
    deleteAttachments,
    announcement,
    isEdit,
  ]);

  const setEmoji = useCallback((e) => {
    setAnnouncementMessage((prevText) => `${prevText}${e.emoji}`);
  }, []);

  return (
    <>
      <DialogRoundedModal
        open={isOpen}
        onClose={onDidDismiss}
        title={isEdit ? texts.title.edit : texts.title.new}
        IconComponent={<GroupsOutlined />}
        maxWidth="md"
        fullWidth
        actionButtons={(
          <SaveButton
            variant="contained"
            onClick={handleSaveAnnouncement}
            disabled={!announcementMessage.trim() || !isMessageValid}
          >
            {isEdit ? texts.button.update : texts.button.create}
          </SaveButton>
        )}
      >
        <ModalBody>
          <InputLabel>{texts.labels.tag}</InputLabel>
          <StyledSelectField
            value={selectedTag}
            onChange={(e) => setSelectedTag(e.target.value)}
          >
            {clientTagsList.map((tag) => (
              <MenuItem key={tag.id} value={tag.id}>
                {tag.label}
              </MenuItem>
            ))}
          </StyledSelectField>
          <InputLabel>{texts.labels.message}</InputLabel>
          <TextField
            value={announcementMessage}
            rows={4}
            multiline
            onChange={(e) => setAnnouncementMessage(e.target.value)}
            error={!isMessageValid}
            helperText={!isMessageValid ? format(texts.maxLength, { maxLength: MAX_MESSAGE_LENGTH }) : ''}
            $withoutMargin
          />
          <RecipientActionsContainer>
            <StyledTypography>
              {format(texts.count, { count: announcementMessage.length, maxLength: MAX_MESSAGE_LENGTH })}
            </StyledTypography>
            <EmojiInput onEmojiClick={setEmoji} />
          </RecipientActionsContainer>
          <AttachmentsContainer
            fileAttachments={fileAttachments}
            onFilesSelected={handleFileSelection}
            onFileRemoved={removeFile}
            isUploading={false}
          />
        </ModalBody>
      </DialogRoundedModal>
      <LoadingOverlay isLoading={isProcessing} />
    </>
  );
};

AnnouncementModal.propTypes = {
  isOpen: PropTypes.bool.isRequired,
  onDidDismiss: PropTypes.func.isRequired,
  announcement: PropTypes.instanceOf(Announcement),
};

AnnouncementModal.defaultProps = {
  announcement: null,
};

export default AnnouncementModal;
